import { useCallback, useMemo } from 'react';
import { NarrativeMiniValuatorForm } from './NarrativeMiniValuatorForm';
import { NarrativeForm } from '@/pages/Narrative/utils/schema';
import {
  NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM,
  NarrativeMiniValuatorScreen,
} from '../utils';
import { NarrativeValuatorCompany } from '@/pages/Narrative/hooks/narrativeValuatorCompanyFragment';
import { useTracking } from 'react-tracking';
import { transformFormValuesToValuation } from '@/pages/Narrative/utils/transformation';
import { getLocationPathname } from '@/redux/location/selectors';
import { ROUTE_PUBLIC_NARRATIVE_EDIT } from '@/constants/routes';
import { getPath } from '@/router/utils';
import { useSelector } from 'react-redux';
import { useUpdatePublicNarrative } from '@/pages/Narrative/hooks/useUpdatePublicNarrative';
import { useNarrativeInvalidate } from '@/pages/Narrative/hooks/useNarrativeInvalidate';
import { usePastAnnualRevenueEarnings } from '@/pages/Narrative/hooks/usePastAnnualRevenueEarnings/usePastAnnualRevenueEarnings';
import { FragmentOf } from 'gql.tada';
import { gql } from '@/utilities/gql';
import {
  CreateNarrativeFuturePEMultipleCalculator,
  ViewEditNarrativeFuturePEMultipleCalculator,
  useFuturePEMultipleCalculation,
  hasOnlyFairValue,
  FuturePEMultipleContext,
  calculateValuationInitialisation,
} from '../../Calculators';

export const NARRATIVE_MINI_VALUATOR_EDIT_NARRATIVE_FRAGMENT = gql(`
  fragment NarrativeMiniValuatorEditNarrativeFields on Narrative @_unmask {
    id
    title
    valuation {
      exchangeRate
      discountRate
      fairValue
      futurePePercentageChange
      futureSharesOutstanding
      profitMarginPercentageChange
      revenuePercentageChange
      timeframe
      fiscalData {
        mostRecentReportedDate
        yearlyTimeSeries {
          year
          data {
            netIncome
            revenue
            endDate
            totalSharesOutstanding
          }
        }
      }
    }
  }
`);

type Props = {
  company: NarrativeValuatorCompany;
  setScreen: (screen: NarrativeMiniValuatorScreen) => void;
  narrative: FragmentOf<typeof NARRATIVE_MINI_VALUATOR_EDIT_NARRATIVE_FRAGMENT>;
};

export const NarrativeMiniValuatorFormEdit = ({
  company,
  setScreen,
  narrative,
}: Props) => {
  const { trackEvent } = useTracking();
  const { invalidatePriority } = useNarrativeInvalidate();

  const { mutate: updateNarrative, isPending } = useUpdatePublicNarrative({
    onSuccess: () => {
      invalidatePriority(['narrative-mini-valuator']);
      trackEvent({
        action: 'save',
      });
    },
  });

  const handleSubmit = useCallback(
    (values: NarrativeForm) => {
      const { id } = company;
      const { title } = values;

      updateNarrative({
        input: {
          id: narrative.id,
          title,
          companyId: id,
          valuations: [
            transformFormValuesToValuation({
              values,
            }),
          ],
        },
      });
    },
    [updateNarrative, narrative, company]
  );

  const strategyContext = useMemo(
    () =>
      new FuturePEMultipleContext(
        hasOnlyFairValue(narrative.valuation)
          ? new CreateNarrativeFuturePEMultipleCalculator(company)
          : new ViewEditNarrativeFuturePEMultipleCalculator(narrative.valuation)
      ),
    [company, narrative.valuation]
  );

  const {
    calculateFairValue,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
  } = useFuturePEMultipleCalculation(strategyContext);

  const initialNarrativeForm: NarrativeForm = useMemo(() => {
    const calculatorOutputs = calculateValuationInitialisation({
      valuation: narrative.valuation,
      calculateFairValueFromInitialisation,
      calculateRevenueGrowthRateFromFairValue,
    });

    return {
      ...calculatorOutputs,
      // Not modified by form, updatedNarrative ignores this field
      userHoldsPosition: null,
      title: narrative.title ?? '',
      content: {
        // Not modified by form, updatedNarrative ignores this field
        text: '',
        html: '',
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrative.valuation, narrative.title]);

  const pathname = useSelector(getLocationPathname);

  const pastAnnualRevenueEarnings = usePastAnnualRevenueEarnings({
    company,
    fiscalData: narrative.valuation.fiscalData,
  });

  return (
    <NarrativeMiniValuatorForm
      company={company}
      pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
      initialValues={initialNarrativeForm}
      setScreen={setScreen}
      onSubmit={handleSubmit}
      isSubmitting={isPending}
      saveLabel="Save"
      enableReinitialize
      fullScreenPath={getPath({
        type: ROUTE_PUBLIC_NARRATIVE_EDIT,
        payload: { id: narrative.id },
        query: {
          company: company.id,
          returnPath: `${pathname}?${NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM}=${company.canonicalURL}`,
        },
      })}
      calculateFairValue={calculateFairValue}
      calculateRevenueGrowthRateFromFairValue={
        calculateRevenueGrowthRateFromFairValue
      }
    />
  );
};
